import clsx from 'clsx';
import { Check, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import AppButton from '../../common/AppButton';
import Badge from '../../common/Badge';

export type SubscriptionPeriod = 'monthly' | 'yearly';

const periodAbbreviations: Record<SubscriptionPeriod, string> = {
  monthly: 'mo',
  yearly: 'yr',
};

export interface SubscriptionFeature {
  label: string;
  included?: boolean;
}

export interface SubscriptionCardProps {
  subscriptionName: string;
  subscriptionPrice: string;
  subscriptionDescription: string;
  subscriptionPeriod?: SubscriptionPeriod;
  subscriptionFeatures?: SubscriptionFeature[];
  billingNote?: string;
  isSelected?: boolean;
  onClick: () => void;
  badgeLabel?: string;
  headerSlot?: ReactNode;
  disabled?: boolean;
  buttonLabel?: string;
  loading?: boolean;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscriptionName,
  subscriptionDescription,
  subscriptionPrice,
  subscriptionPeriod = 'monthly',
  subscriptionFeatures,
  billingNote,
  isSelected,
  onClick,
  badgeLabel,
  headerSlot,
  disabled = false,
  buttonLabel,
  loading = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const selectedCardStyles = clsx(
    'border-2 border-electric bg-white shadow-[0px_4px_6px_-2px_#0000000D,0px_10px_15px_-3px_#0000001A]',
    !headerSlot && 'lg:bg-cloud'
  );

  const unselectedCardStyles =
    'border-grey3 bg-white shadow-[0px_1px_2px_0px_#0000000F,0px_1px_3px_0px_#0000001A]';

  const renderSubscriptionPrice = () => {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-end text-dark">
          <h1
            className={clsx(
              'text-4xl font-bold',
              isSelected ? 'text-primary' : 'text-dark'
            )}
          >
            {subscriptionPeriod === 'yearly'
              ? '$' +
                (Number(subscriptionPrice.replace(/^\$/, '')) / 12).toFixed(2)
              : subscriptionPrice}
          </h1>
          <span className="text-2xl font-bold text-light">
            /{periodAbbreviations.monthly}
          </span>
        </div>
        {subscriptionPeriod === 'yearly' && (
          <div className="text-sm text-light">
            {subscriptionPrice}/year, billed annually upfront
          </div>
        )}
        {billingNote && <div className="text-sm text-light">{billingNote}</div>}
      </div>
    );
  };

  const renderFeature = ({ label, included = true }: SubscriptionFeature) => (
    <div key={label} className="flex items-start gap-1.5 px-1">
      <span
        className={clsx(
          'flex items-center justify-center w-5 h-5 rounded-[5px] border-[1.5px] shrink-0',
          included
            ? 'bg-electric border-electric text-white'
            : 'bg-white border-grey2 text-grey2'
        )}
      >
        {included ? (
          <Check className="w-3.5 h-3.5 stroke-[3]" aria-hidden />
        ) : (
          <X className="w-3.5 h-3.5 stroke-[3]" aria-hidden />
        )}
      </span>
      <span
        className={clsx(
          'text-sm leading-snug',
          included ? 'text-dark' : 'text-grey1'
        )}
      >
        {label}
      </span>
    </div>
  );

  return (
    <div
      className={clsx(
        'relative flex flex-col w-full lg:w-64 h-auto lg:min-h-[540px] p-6 rounded-xl border',
        isSelected ? selectedCardStyles : unselectedCardStyles
      )}
    >
      {badgeLabel && (
        <Badge
          shape="pill"
          className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 font-semibold"
        >
          {badgeLabel}
        </Badge>
      )}

      {headerSlot && (
        <div className="mb-8 flex justify-center">{headerSlot}</div>
      )}

      <div className="flex flex-col gap-3">
        <div className="text-2xl font-bold text-dark">{subscriptionName}</div>
        <div className="text-sm text-dark">{subscriptionDescription}</div>
        {renderSubscriptionPrice()}
        {subscriptionFeatures && subscriptionFeatures.length > 0 && (
          <div className="flex flex-col gap-2">
            {subscriptionFeatures.map(renderFeature)}
          </div>
        )}
      </div>

      <div className="mt-auto pt-8">
        <AppButton
          variant={isSelected ? 'primary' : 'secondary'}
          size="lg"
          fullWidth
          loading={loading || isLoading}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            setIsLoading(true);
            onClick?.();
            setTimeout(() => {
              setIsLoading(false);
            }, 300);
          }}
        >
          {buttonLabel || (isLoading ? 'Loading...' : 'START FREE TRIAL')}
        </AppButton>
      </div>
    </div>
  );
};

export default SubscriptionCard;
